/**
 * FiasAIContext - React context bridging arche state and Fias AI.
 *
 * When an arche enables Fias AI integration, this context provides
 * the bridge between the arche's state management and the Fias AI popup.
 */

import { createContext, useContext } from 'react';
import { useFiasAIArche } from './useFiasAIArche';
import type { FiasAIChatMessage } from './useFiasAIChat';
import type { ArcheAICapabilities } from '../types';

interface FiasAIContextValue {
  capabilities: ArcheAICapabilities | null;
  isPanelOpen: boolean;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  messages: FiasAIChatMessage[];
  isStreaming: boolean;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  submit: (message: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  applyProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string) => void;
}

const FiasAIContext = createContext<FiasAIContextValue | null>(null);

export function useFiasAIContext(): FiasAIContextValue {
  const context = useContext(FiasAIContext);
  if (!context) {
    throw new Error('useFiasAIContext must be used within a FiasAIProvider');
  }
  return context;
}

interface FiasAIProviderProps {
  capabilities: ArcheAICapabilities;
  children: React.ReactNode;
}

/**
 * Provider that wraps an arche to enable Fias AI integration.
 * Manages chat state, streaming, and proposals via useFiasAIArche.
 */
export function FiasAIProvider({ capabilities, children }: FiasAIProviderProps) {
  const arche = useFiasAIArche({ capabilities });

  const value: FiasAIContextValue = {
    capabilities,
    isPanelOpen: arche.isPanelOpen,
    togglePanel: arche.togglePanel,
    openPanel: arche.openPanel,
    closePanel: arche.closePanel,
    messages: arche.messages,
    isStreaming: arche.isStreaming,
    currentModel: arche.currentModel,
    submit: arche.submit,
    stop: arche.stop,
    reset: arche.reset,
    applyProposal: arche.applyProposal,
    rejectProposal: arche.rejectProposal,
  };

  return <FiasAIContext.Provider value={value}>{children}</FiasAIContext.Provider>;
}
